import { ImageOff, LoaderCircle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

/**
 * An image that needs the bearer token to fetch, so it cannot be a plain src: loaded as a blob and
 * shown from an object URL, which is released when the image goes away.
 */
export function useBlobUrl(load: () => Promise<Blob>, key: string) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    let made: string | null = null
    setUrl(null)
    setFailed(false)
    load()
      .then((blob) => {
        if (!live) return
        made = URL.createObjectURL(blob)
        setUrl(made)
      })
      .catch(() => live && setFailed(true))
    return () => {
      live = false
      if (made) URL.revokeObjectURL(made)
    }
    // key names the image; load is a fresh closure each render and would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return { url, failed }
}

/** A square thumbnail that opens full size on click. */
export function AuthedThumb({
  load,
  imageKey,
  alt,
  className = 'h-24 w-24',
}: {
  load: () => Promise<Blob>
  imageKey: string
  alt: string
  className?: string
}) {
  const { url, failed } = useBlobUrl(load, imageKey)
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => url && setOpen(true)}
        disabled={!url}
        aria-label={`View ${alt} full size`}
        className={`${className} grid shrink-0 place-items-center overflow-hidden rounded-lg border border-ink-200 bg-ink-100 text-ink-400`}
      >
        {url ? (
          <img src={url} alt={alt} className="h-full w-full object-cover" />
        ) : failed ? (
          <ImageOff size={18} />
        ) : (
          <LoaderCircle size={18} className="animate-spin" />
        )}
      </button>
      {open && url && <Lightbox url={url} alt={alt} onClose={() => setOpen(false)} />}
    </>
  )
}

export function Lightbox({ url, alt, onClose }: { url: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[1300] grid place-items-center bg-ink-900/80 p-4"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-ink-900 hover:bg-white"
      >
        <X size={18} />
      </button>
      <img
        src={url}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[95vw] rounded-lg object-contain shadow-float"
      />
    </div>
  )
}
